/**
 * Copyright 2025 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import React, { ReactElement } from 'react';
import { Card } from 'react-bootstrap';
import { Section } from 'templates/pubSub/types/TemplateType';
import { TemplateContext } from 'templates/pubSub/types/TemplateContextType';
import { NodeIdentifier } from 'utilities/PubSub/PubSubAddressSpace';
import SectionContent from './SectionContent';
import TemplateHelperPubSub from 'utilities/TemplateHelperPubSub';
import { usePubSubNode, usePubSubValue } from 'utilities/PubSub/PubSubHooks';

interface ISectionCardProps {
  templateContext: TemplateContext;
  section: Section;
  startingNodeId?: NodeIdentifier;
}

export default function SectionCard(props: ISectionCardProps): ReactElement | null {
  const validationNodePath = TemplateHelperPubSub.resolveSectionValidationNodePath(props.section);

  const validationNodeFilters = TemplateHelperPubSub.createFilter(props.templateContext.namespaces, validationNodePath);

  const validationNode = usePubSubNode(validationNodeFilters, props.startingNodeId);

  const filterTitle = TemplateHelperPubSub.createFilter(
    props.templateContext.namespaces,
    props.section.titleVariable?.variablePath,
  );

  const nodeTitle = usePubSubNode(filterTitle, props.startingNodeId);

  const valueTitle = usePubSubValue(nodeTitle);

  const shouldHideSection = TemplateHelperPubSub.shouldHideOptionalSection(
    validationNode,
    props.section,
    `SectionCard:${props.section.id}`,
  );

  if (shouldHideSection) {
    return null;
  }

  const colStyle = 'col-4 col-sm-4 col-md-4';

  const title = valueTitle !== undefined ? valueTitle : props.section.title;

  return (
    <Card className="mb-4">
      <Card.Header>
        <h3 className="h5 mb-0">{title}</h3>
      </Card.Header>

      <Card.Body>
        <SectionContent
          templateContext={props.templateContext}
          cols={colStyle}
          section={props.section}
          startingNodeId={props.startingNodeId}
        />
      </Card.Body>
    </Card>
  );
}
